import { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { isFormStylingApplied } from '../../utils/sectionFormStyling'

export default function PublicPointsTable({ website, section, sectionFormStyling, useDefaultStyling = true, sectionType = 'points-table' }) {
  const [standings, setStandings] = useState([])
  const [loading, setLoading] = useState(true)
  const isFormStyling = useDefaultStyling || isFormStylingApplied(sectionFormStyling, sectionType, section?._id)
  
  // Default values to fallback to if styling is not provided
  const DEFAULT_FORM_COLORS = {
    backgroundColor: '#FFFFFF',
    textColor: '#111827',
    hoverBackgroundColor: '#F9FAFB',
    hoverTextColor: '#374151',
    buttonBackgroundColor: '#3B82F6',
    buttonTextColor: '#FFFFFF',
    hoverButtonBackgroundColor: '#2563eb',
    hoverButtonTextColor: '#ffffff'
  }
  
  // Ensure we have valid styling values
  const formColors = isFormStyling ? (sectionFormStyling || DEFAULT_FORM_COLORS) : undefined
  
  const formStyle = isFormStyling ? {
    backgroundColor: formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor,
    color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor
  } : undefined
  const textStyle = isFormStyling ? { color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor } : undefined

  useEffect(() => {
    fetchStandings()
  }, [website])

  const fetchStandings = async () => {
    try {
      setLoading(true)
      const response = await axios.get(`/fixtures/standings/${website._id}`)
      setStandings(response.data.standings || [])
    } catch (error) {
      console.error('[PublicPointsTable] Error loading:', error)
      toast.error('Failed to load league standings')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (standings.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <p style={textStyle}>League table will be updated as matches are completed</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto" style={formStyle}>
      <table className="w-full">
        <thead className="bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-300">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-bold" style={textStyle}>Pos</th>
            <th className="px-4 py-3 text-left text-sm font-bold" style={textStyle}>Team</th>
            <th className="px-4 py-3 text-center text-sm font-bold" style={textStyle}>Played</th>
            <th className="px-4 py-3 text-center text-sm font-bold" style={textStyle}>Wins</th>
            <th className="px-4 py-3 text-center text-sm font-bold" style={textStyle}>Draws</th>
            <th className="px-4 py-3 text-center text-sm font-bold" style={textStyle}>Losses</th>
            <th className="px-4 py-3 text-center text-sm font-bold" style={textStyle}>GF</th>
            <th className="px-4 py-3 text-center text-sm font-bold" style={textStyle}>GA</th>
            <th className="px-4 py-3 text-center text-sm font-bold" style={textStyle}>GD</th>
            <th className="px-4 py-3 text-center text-sm font-bold text-white bg-primary-600 rounded-tr-lg">Pts</th>
          </tr>
        </thead>
        <tbody>
          {standings.map((standing, idx) => (
            <tr key={standing._id} className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${
              idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
            }`}>
              {/* Position */}
              <td className="px-4 py-3">
                <div className="flex items-center justify-center">
                  <span className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${
                    idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                    idx === 1 ? 'bg-gray-400 text-white' :
                    idx === 2 ? 'bg-orange-400 text-white' :
                    'bg-gray-200 text-gray-700'
                  }`}>
                    {standing.position}
                  </span>
                </div>
              </td>

              {/* Team Name */}
              <td className="px-4 py-3">
                <p className="font-semibold" style={textStyle}>{standing.teamName}</p>
              </td>

              {/* Played */}
              <td className="px-4 py-3 text-center text-sm font-medium" style={textStyle}>
                {standing.played}
              </td>

              {/* Wins */}
              <td className="px-4 py-3 text-center text-sm font-medium text-green-700 bg-green-50">
                {standing.wins}
              </td>

              {/* Draws */}
              <td className="px-4 py-3 text-center text-sm font-medium text-blue-700 bg-blue-50">
                {standing.draws}
              </td>

              {/* Losses */}
              <td className="px-4 py-3 text-center text-sm font-medium text-red-700 bg-red-50">
                {standing.losses}
              </td>

              {/* Goals For */}
              <td className="px-4 py-3 text-center text-sm font-semibold" style={textStyle}>
                {standing.goalsFor}
              </td>

              {/* Goals Against */}
              <td className="px-4 py-3 text-center text-sm font-semibold" style={textStyle}>
                {standing.goalsAgainst}
              </td>

              {/* Goal Difference */}
              <td className="px-4 py-3 text-center text-sm font-semibold" style={textStyle}>
                <span className={`inline-block px-2 py-1 rounded ${
                  standing.goalDifference > 0 ? 'bg-green-100 text-green-800' :
                  standing.goalDifference < 0 ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {standing.goalDifference > 0 ? '+' : ''}{standing.goalDifference}
                </span>
              </td>

              {/* Points */}
              <td className="px-4 py-3 text-center text-sm font-bold text-white bg-primary-600">
                {standing.points}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
